import { useFormContext } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

export type BillingInfoPrivate = {
  type: "private";
  first_name: string;
  last_name: string;
  address: string;
  codice_fiscale: string;
};

export type BillingInfoCompany = {
  type: "company";
  business_name_or_full_name: string;
  address: string;
  codice_univoco_sdi: string;
};

export type BillingInfo = BillingInfoPrivate | BillingInfoCompany;

const cfRegex = /^[A-Za-z]{6}[0-9]{2}[A-Za-z][0-9]{2}[A-Za-z][0-9]{3}[A-Za-z]$/;
const codiceUnivocoRegex = /^[A-Za-z0-9]{7}$/;

export function validateBillingInfo(billing: BillingInfo | null | undefined): string | null {
  if (!billing || !billing.type) return "Seleziona il tipo di fatturazione e compila i campi.";
  if (billing.type === "private") {
    const p = billing as BillingInfoPrivate;
    if (!p.first_name?.trim()) return "Nome obbligatorio per fatturazione.";
    if (!p.last_name?.trim()) return "Cognome obbligatorio per fatturazione.";
    if (!p.address?.trim() || p.address.length < 10) return "Indirizzo di fatturazione obbligatorio (via e numero).";
    if (!p.codice_fiscale?.trim()) return "Codice Fiscale obbligatorio.";
    const cf = p.codice_fiscale.trim().toUpperCase();
    if (!cfRegex.test(cf)) return "Codice Fiscale non valido (16 caratteri).";
    return null;
  }
  if (billing.type === "company") {
    const c = billing as BillingInfoCompany;
    if (!c.business_name_or_full_name?.trim()) return "Ragione sociale o Nome e Cognome obbligatori.";
    if (!c.address?.trim() || c.address.length < 10) return "Indirizzo (sede) obbligatorio.";
    if (!c.codice_univoco_sdi?.trim()) return "Codice Univoco SDI obbligatorio (7 caratteri).";
    const cu = c.codice_univoco_sdi.trim().toUpperCase();
    if (!codiceUnivocoRegex.test(cu)) return "Codice Univoco SDI non valido (7 caratteri alfanumerici).";
    return null;
  }
  return "Tipo fatturazione non valido.";
}

interface InvoiceBillingFormProps {
  defaultBilling?: BillingInfo | null;
  onBillingChange?: (billing: BillingInfo | null) => void;
  className?: string;
  /** When true, show "Salva dati per le prossime prenotazioni" */
  showSaveToProfile?: boolean;
}

export default function InvoiceBillingForm({
  defaultBilling,
  onBillingChange,
  className,
  showSaveToProfile = true,
}: InvoiceBillingFormProps) {
  const form = useFormContext();
  const billingType = form.watch("billingType") as "private" | "company" | undefined;
  const saveBillingToProfile = form.watch("saveBillingToProfile");

  const setBilling = (type: "private" | "company", field: string, value: string) => {
    const current = form.getValues("billingInfo") as BillingInfo | null | undefined;
    const next: BillingInfo =
      type === "private"
        ? {
            type: "private",
            first_name: type === "private" && field === "first_name" ? value : (current as BillingInfoPrivate)?.first_name ?? "",
            last_name: type === "private" && field === "last_name" ? value : (current as BillingInfoPrivate)?.last_name ?? "",
            address: type === "private" && field === "address" ? value : (current as BillingInfoPrivate)?.address ?? "",
            codice_fiscale: type === "private" && field === "codice_fiscale" ? value : (current as BillingInfoPrivate)?.codice_fiscale ?? "",
          }
        : {
            type: "company",
            business_name_or_full_name: type === "company" && field === "business_name_or_full_name" ? value : (current as BillingInfoCompany)?.business_name_or_full_name ?? "",
            address: type === "company" && field === "address" ? value : (current as BillingInfoCompany)?.address ?? "",
            codice_univoco_sdi: type === "company" && field === "codice_univoco_sdi" ? value : (current as BillingInfoCompany)?.codice_univoco_sdi ?? "",
          };
    form.setValue("billingInfo", next);
    onBillingChange?.(next);
  };

  const setType = (type: "private" | "company") => {
    form.setValue("billingType", type);
    const current = form.getValues("billingInfo");
    const defaultPrivate = defaultBilling?.type === "private" ? defaultBilling : null;
    const defaultCompany = defaultBilling?.type === "company" ? defaultBilling : null;
    if (type === "private") {
      const prev = current as BillingInfoPrivate | undefined;
      const next: BillingInfoPrivate = {
        type: "private",
        first_name: prev?.first_name ?? defaultPrivate?.first_name ?? "",
        last_name: prev?.last_name ?? defaultPrivate?.last_name ?? "",
        address: prev?.address ?? defaultPrivate?.address ?? "",
        codice_fiscale: prev?.codice_fiscale ?? defaultPrivate?.codice_fiscale ?? "",
      };
      form.setValue("billingInfo", next);
      onBillingChange?.(next);
    } else {
      const prev = current as BillingInfoCompany | undefined;
      const next: BillingInfoCompany = {
        type: "company",
        business_name_or_full_name: prev?.business_name_or_full_name ?? defaultCompany?.business_name_or_full_name ?? "",
        address: prev?.address ?? defaultCompany?.address ?? "",
        codice_univoco_sdi: prev?.codice_univoco_sdi ?? defaultCompany?.codice_univoco_sdi ?? "",
      };
      form.setValue("billingInfo", next);
      onBillingChange?.(next);
    }
  };

  return (
    <div className={cn("space-y-4", className)}>
      <div>
        <Label className="text-sm font-medium">Tipo di fatturazione</Label>
        <RadioGroup
          value={billingType || "private"}
          onValueChange={(v) => setType(v as "private" | "company")}
          className="mt-2 flex flex-wrap gap-4"
        >
          <label className="flex items-center gap-2 cursor-pointer">
            <RadioGroupItem value="private" />
            <span className="text-sm">Privato (persona fisica)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <RadioGroupItem value="company" />
            <span className="text-sm">Azienda / Partita IVA</span>
          </label>
        </RadioGroup>
      </div>

      {billingType === "private" && (
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="billing-first-name" className="text-sm">Nome *</Label>
              <Input
                id="billing-first-name"
                className="mt-1"
                value={(form.watch("billingInfo") as BillingInfoPrivate)?.first_name ?? ""}
                onChange={(e) => setBilling("private", "first_name", e.target.value)}
                placeholder="Mario"
              />
            </div>
            <div>
              <Label htmlFor="billing-last-name" className="text-sm">Cognome *</Label>
              <Input
                id="billing-last-name"
                className="mt-1"
                value={(form.watch("billingInfo") as BillingInfoPrivate)?.last_name ?? ""}
                onChange={(e) => setBilling("private", "last_name", e.target.value)}
                placeholder="Rossi"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="billing-address-private" className="text-sm">Indirizzo di fatturazione (via e numero) *</Label>
            <Input
              id="billing-address-private"
              className="mt-1"
              value={(form.watch("billingInfo") as BillingInfoPrivate)?.address ?? ""}
              onChange={(e) => setBilling("private", "address", e.target.value)}
              placeholder="Via Roma 1, 00100 Roma"
            />
          </div>
          <div>
            <Label htmlFor="billing-cf" className="text-sm">Codice Fiscale *</Label>
            <Input
              id="billing-cf"
              className="mt-1 font-mono uppercase"
              value={(form.watch("billingInfo") as BillingInfoPrivate)?.codice_fiscale ?? ""}
              onChange={(e) => setBilling("private", "codice_fiscale", e.target.value.toUpperCase())}
              placeholder="RSSMRA80A01H501Z"
              maxLength={16}
            />
          </div>
        </div>
      )}

      {billingType === "company" && (
        <div className="grid gap-3">
          <div>
            <Label htmlFor="billing-business-name" className="text-sm">Ragione sociale oppure Nome e Cognome *</Label>
            <Input
              id="billing-business-name"
              className="mt-1"
              value={(form.watch("billingInfo") as BillingInfoCompany)?.business_name_or_full_name ?? ""}
              onChange={(e) => setBilling("company", "business_name_or_full_name", e.target.value)}
              placeholder="Es. Acme S.r.l. oppure Mario Rossi"
            />
          </div>
          <div>
            <Label htmlFor="billing-address-company" className="text-sm">Indirizzo della sede / Partita IVA *</Label>
            <Input
              id="billing-address-company"
              className="mt-1"
              value={(form.watch("billingInfo") as BillingInfoCompany)?.address ?? ""}
              onChange={(e) => setBilling("company", "address", e.target.value)}
              placeholder="Via Roma 1, 00100 Roma"
            />
          </div>
          <div>
            <Label htmlFor="billing-sdi" className="text-sm">Codice Univoco SDI (7 caratteri) *</Label>
            <Input
              id="billing-sdi"
              className="mt-1 font-mono uppercase"
              value={(form.watch("billingInfo") as BillingInfoCompany)?.codice_univoco_sdi ?? ""}
              onChange={(e) => setBilling("company", "codice_univoco_sdi", e.target.value.toUpperCase().slice(0, 7))}
              placeholder="M5UXCR1"
              maxLength={7}
            />
          </div>
        </div>
      )}

      {showSaveToProfile && (
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox
            checked={!!saveBillingToProfile}
            onCheckedChange={(checked) => form.setValue("saveBillingToProfile", !!checked)}
          />
          <span className="text-sm text-muted-foreground">Salva i dati di fatturazione nel profilo per le prossime prenotazioni</span>
        </label>
      )}
    </div>
  );
}
